import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { H1Title, Paragraph } from "../../components/typography/typography";
import { AppButton } from "../../components/buttons/button";
import SubmissionService from "../../services/submissionService";
import CompetitionService from "../../services/competitionService";
import api from "../../services/api";
import { Success, Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag, faCheckCircle, faClock, faSpinner, faExclamationTriangle, faChevronDown, faChevronRight, faEye, faPlus, faTrash, faPen, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";

const MILESTONE_STATUS = {
  pending: { icon: faClock, color: 'text-grey-667', bg: 'bg-grey-200', label: 'Pending' },
  in_progress: { icon: faSpinner, color: 'text-primary', bg: 'bg-primary-bg', label: 'In Progress' },
  completed: { icon: faCheckCircle, color: 'text-green', bg: 'bg-green-light', label: 'Completed' },
  delayed: { icon: faExclamationTriangle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'Delayed' },
};

export default function AdminImplementation() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [competitionId, setCompetitionId] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [addingFor, setAddingFor] = useState(null); // submissionId whose add form is open
  const [newMilestone, setNewMilestone] = useState({ title: '', description: '', targetDate: '' });
  const [savingFor, setSavingFor] = useState(null);
  const [editingMilestoneId, setEditingMilestoneId] = useState(null);
  const [editDraft, setEditDraft] = useState({ progressNotes: '', budgetSpent: '', status: 'pending' });

  useEffect(() => { loadCompetition(); }, []);
  useEffect(() => { if (competitionId) loadSubmissions(); }, [competitionId]);

  const loadCompetition = async () => {
    try {
      const { data } = await CompetitionService.getActive();
      setCompetitionId(data.data.competition._id);
    } catch {
      ToastError('No active competition found');
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const { data } = await SubmissionService.getAll({
        competitionId,
        currentRound: 'implementation',
        limit: 200
      });
      setSubmissions(data.data.submissions || []);
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to load');
    } finally { setLoading(false); }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const saveMilestones = async (submissionId, milestones) => {
    setSavingFor(submissionId);
    try {
      await api.put(`/submissions/${submissionId}/milestones`, { milestones });
      await loadSubmissions();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to save milestones');
    } finally { setSavingFor(null); }
  };

  const handleAddMilestone = async (sub) => {
    if (!newMilestone.title.trim()) { ToastError('Milestone title required'); return; }
    const current = (sub.implementation?.milestones || []).map(m => ({
      title: m.title,
      description: m.description,
      targetDate: m.targetDate,
      completedDate: m.completedDate,
      status: m.status,
      progressNotes: m.progressNotes,
      budgetSpent: m.budgetSpent
    }));
    const next = [...current, {
      title: newMilestone.title.trim(),
      description: newMilestone.description.trim(),
      targetDate: newMilestone.targetDate || undefined,
      status: 'pending'
    }];
    await saveMilestones(sub._id, next);
    setNewMilestone({ title: '', description: '', targetDate: '' });
    setAddingFor(null);
    Success('Milestone added');
  };

  const startEditMilestone = (m) => {
    setEditingMilestoneId(m._id);
    setEditDraft({
      progressNotes: m.progressNotes || '',
      budgetSpent: m.budgetSpent != null ? String(m.budgetSpent) : '',
      status: m.status || 'pending'
    });
  };

  const saveEditMilestone = async (sub, milestoneId) => {
    setSavingFor(sub._id);
    try {
      await api.put(`/submissions/${sub._id}/milestones/${milestoneId}`, {
        progressNotes: editDraft.progressNotes,
        budgetSpent: editDraft.budgetSpent === '' ? 0 : parseFloat(editDraft.budgetSpent),
        status: editDraft.status
      });
      setEditingMilestoneId(null);
      await loadSubmissions();
      Success('Milestone updated');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to save');
    } finally { setSavingFor(null); }
  };

  const handleDeleteMilestone = async (sub, milestoneId) => {
    if (!confirm('Remove this milestone?')) return;
    const next = (sub.implementation?.milestones || [])
      .filter(m => m._id !== milestoneId)
      .map(m => ({
        title: m.title,
        description: m.description,
        targetDate: m.targetDate,
        completedDate: m.completedDate,
        status: m.status,
        progressNotes: m.progressNotes,
        budgetSpent: m.budgetSpent
      }));
    await saveMilestones(sub._id, next);
    Success('Milestone removed');
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <H1Title size="sm">Implementation Monitoring</H1Title>
        <Paragraph size="sm" className="text-grey-667">
          {submissions.length} team{submissions.length === 1 ? '' : 's'} in the implementation phase
        </Paragraph>
      </div>

      {submissions.length === 0 ? (
        <div className="bg-white rounded-xl border border-grey-200 p-12 text-center">
          <FontAwesomeIcon icon={faFlag} className="text-grey-200 text-3xl mb-3" />
          <Paragraph className="text-grey-667">
            No teams have started implementation yet. Select a winner on the Submissions page, then click <strong>Start Implementation</strong> on their detail view to move them here.
          </Paragraph>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map((sub) => {
            const isOpen = expanded.has(sub._id);
            const impl = sub.implementation || {};
            const milestones = impl.milestones || [];
            const updates = impl.monthlyUpdates || [];
            const completedCount = milestones.filter(m => m.status === 'completed').length;
            const totalSpent = milestones.reduce((n, m) => n + (m.budgetSpent || 0), 0);

            return (
              <div key={sub._id} className="bg-white rounded-xl border border-grey-200 overflow-hidden">
                <button
                  onClick={() => toggleExpand(sub._id)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-grey-bg/50 cursor-pointer text-left"
                >
                  <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="text-grey-98 text-xs flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {sub.submitterId?.firstName} {sub.submitterId?.lastName}
                      {sub.isWinner && <span className="ml-2 text-xs bg-yellow-500/10 text-yellow-600 px-2 py-0.5 rounded-full">Winner</span>}
                    </p>
                    <p className="text-xs text-grey-98 truncate">{sub.teamId?.name || 'Individual'} · {sub.submitterId?.email}</p>
                  </div>
                  <div className="flex gap-4 text-xs text-grey-667 flex-shrink-0">
                    <span><strong className="text-grey-dark">{completedCount}</strong>/{milestones.length} milestones</span>
                    <span><strong className="text-grey-dark">{updates.length}</strong> updates</span>
                    <span>Spent: <strong className="text-grey-dark">${totalSpent.toLocaleString()}</strong></span>
                  </div>
                  <Link
                    to={`/admin/submissions/${sub._id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-primary hover:underline text-xs inline-flex items-center gap-1 flex-shrink-0"
                  >
                    <FontAwesomeIcon icon={faEye} /> View
                  </Link>
                </button>

                {isOpen && (
                  <div className="border-t border-grey-200 px-5 py-4 bg-grey-bg/30 space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-sm">Milestones</h4>
                        <AppButton
                          onClick={() => { setAddingFor(addingFor === sub._id ? null : sub._id); setNewMilestone({ title: '', description: '', targetDate: '' }); }}
                          variant="outline"
                          size="sm"
                          fullWidth={false}
                          className="px-3"
                        >
                          <FontAwesomeIcon icon={faPlus} className="mr-1" /> {addingFor === sub._id ? 'Cancel' : 'Add Milestone'}
                        </AppButton>
                      </div>

                      {addingFor === sub._id && (
                        <div className="bg-white border border-primary/30 rounded-lg p-3 mb-3 space-y-2">
                          <input
                            type="text"
                            placeholder="Milestone title (e.g. 'Procure water filters')"
                            className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary"
                            value={newMilestone.title}
                            onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                          />
                          <textarea
                            placeholder="Description (optional)"
                            className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary resize-y min-h-[60px]"
                            value={newMilestone.description}
                            onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                          />
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-grey-667">Target date:</label>
                            <input
                              type="date"
                              className="border border-grey-200 rounded-md px-2 py-1 text-sm outline-none focus:border-primary"
                              value={newMilestone.targetDate}
                              onChange={(e) => setNewMilestone({ ...newMilestone, targetDate: e.target.value })}
                            />
                          </div>
                          <div className="flex justify-end">
                            <AppButton
                              onClick={() => handleAddMilestone(sub)}
                              loading={savingFor === sub._id}
                              size="sm"
                              fullWidth={false}
                              className="px-4"
                            >
                              Save Milestone
                            </AppButton>
                          </div>
                        </div>
                      )}

                      {milestones.length === 0 && addingFor !== sub._id ? (
                        <p className="text-xs text-grey-98 italic">No milestones set yet. Click <strong>Add Milestone</strong> to create the first one — participants cannot track progress until at least one exists.</p>
                      ) : (
                        <div className="space-y-2">
                          {milestones.map((m) => {
                            const cfg = MILESTONE_STATUS[m.status] || MILESTONE_STATUS.pending;
                            const isEditing = editingMilestoneId === m._id;
                            const busy = savingFor === sub._id;
                            return (
                              <div key={m._id} className="flex items-start gap-3 bg-white border border-grey-200 rounded-lg p-3">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${cfg.bg}`}>
                                  <FontAwesomeIcon icon={cfg.icon} className={`${cfg.color} text-xs`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-semibold">{m.title}</p>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                                      {!isEditing && (
                                        <button onClick={() => startEditMilestone(m)} className="text-grey-98 hover:text-primary cursor-pointer" title="Edit status / notes / budget">
                                          <FontAwesomeIcon icon={faPen} className="text-xs" />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleDeleteMilestone(sub, m._id)}
                                        disabled={busy}
                                        className="text-grey-98 hover:text-red-500 cursor-pointer"
                                        title="Remove milestone"
                                      >
                                        <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                      </button>
                                    </div>
                                  </div>
                                  {m.description && <p className="text-xs text-grey-667 mt-0.5">{m.description}</p>}
                                  {m.targetDate && (
                                    <p className="text-xs text-grey-98 mt-1">Target: {new Date(m.targetDate).toLocaleDateString()}</p>
                                  )}

                                  {isEditing ? (
                                    <div className="mt-3 bg-grey-bg rounded-md p-3 space-y-2">
                                      <div>
                                        <label className="block text-xs text-grey-667 mb-1">Status</label>
                                        <select
                                          className="w-full border border-grey-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-primary bg-white"
                                          value={editDraft.status}
                                          onChange={(e) => setEditDraft({ ...editDraft, status: e.target.value })}
                                        >
                                          <option value="pending">Pending</option>
                                          <option value="in_progress">In Progress</option>
                                          <option value="completed">Completed</option>
                                          <option value="delayed">Delayed</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-xs text-grey-667 mb-1">Progress notes</label>
                                        <textarea
                                          className="w-full border border-grey-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-primary bg-white resize-y min-h-[60px]"
                                          value={editDraft.progressNotes}
                                          onChange={(e) => setEditDraft({ ...editDraft, progressNotes: e.target.value })}
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs text-grey-667 mb-1">Budget spent ($)</label>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          className="w-full border border-grey-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-primary bg-white"
                                          value={editDraft.budgetSpent}
                                          onChange={(e) => setEditDraft({ ...editDraft, budgetSpent: e.target.value })}
                                        />
                                      </div>
                                      <div className="flex gap-2 justify-end">
                                        <button onClick={() => setEditingMilestoneId(null)} className="text-xs text-grey-667 hover:text-grey-dark px-3 py-1.5 cursor-pointer">
                                          <FontAwesomeIcon icon={faTimes} className="mr-1" /> Cancel
                                        </button>
                                        <AppButton onClick={() => saveEditMilestone(sub, m._id)} loading={busy} size="sm" fullWidth={false} className="px-3">
                                          <FontAwesomeIcon icon={faSave} className="mr-1" /> Save
                                        </AppButton>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      {m.progressNotes && <p className="text-xs text-grey-98 italic mt-1">"{m.progressNotes}"</p>}
                                      {m.budgetSpent > 0 && (
                                        <p className="text-xs text-grey-dark mt-1">Spent: <strong>${m.budgetSpent.toLocaleString()}</strong></p>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div>
                      <h4 className="font-bold text-sm mb-2">Monthly Updates</h4>
                      {updates.length === 0 ? (
                        <p className="text-xs text-grey-98 italic">No monthly updates submitted yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {[...updates].reverse().map((u, i) => (
                            <div key={i} className="bg-white border-l-2 border-primary pl-3 py-2 rounded-r">
                              <p className="text-xs text-grey-98 mb-1">
                                {new Date(u.submittedAt || u.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                              </p>
                              <p className="text-sm text-grey-dark whitespace-pre-wrap">{u.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
